import React, { useEffect, useState } from 'react';
import Web3 from 'web3';
import { dateFromUtcEpoch, groupBy, log, secondsToDateTime } from '../../Common/HelperFunctions';
import WalletConnectProvider from "@walletconnect/web3-provider";

import { BinanceTickerApiResponse, ExoPresaleRound, ExoPresaleTransaction, SmartContractAddressMap, TokenInformationResponse } from '../../Common/Models';
import { get } from 'https';

interface Web3ProviderProps { }

export type Web3ProviderContextType = {
    Web3Connection: Web3 | null | undefined,
    connectWeb3: (provider: string | undefined) => void;
    disconnectWeb3: () => void;
    web3Connected: boolean;
    currentChainId: number;
    walletAddress: string;
    providerName: string;

    isAdmin: boolean;

    busdBalance: number;
    bnbBalance: number;
    bnbUsdtPrice: number;
    busdApprovalAmount: number;
    approveBusd: () => Promise<void>;

    setReferrerString: (referrer: string) => Promise<void>;

    //presale
    presaleTransactions: ExoPresaleTransaction[],
    presaleRounds: ExoPresaleRound[],
    contribute: (round: number, amount: number, reference: string) => Promise<void>;
}

export const Web3Context = React.createContext<Web3ProviderContextType>({
    Web3Connection: null,
    currentChainId: 1,
    connectWeb3: () => console.warn('method not set up...'),
    disconnectWeb3: () => console.warn('method not set up...'),
    web3Connected: false,
    walletAddress: "",
    providerName: "",

    isAdmin: false,

    busdBalance: 0,
    bnbBalance: 0,
    bnbUsdtPrice: 0,
    busdApprovalAmount: 0,
    approveBusd: async () => console.warn("method not set up..."),

    presaleTransactions: [],
    presaleRounds: [],
    setReferrerString: async (referrer: string) => console.warn("method not set up..."),
    contribute: async (round, amount, reference) => console.warn('method not set up...'),
});

const smartContractAddressMap: SmartContractAddressMap[] = [
    {
        chainId: 56,
        chainName: "mainnet",
        factor: 0.000000000000000001,
        exoPresaleContractAbi: require("./abi/exopresale.abi.json"),
        exoPresaleContractAddress: "0x49100A89694329A4b9F00E360f72266FE3B99023",
        busdTokenAddress: "0xe9e7cea3dedca5984780bafc599bd69add087d56",
        busdTokenAbi: require("./abi/ibep20.abi.json"),

    },
    {
        chainId: 97,
        chainName: "mainnet",
        factor: 1,
        exoPresaleContractAddress: "0xd7b2e0869C41FACE6D918Ae799BeD5b2B7aD93dd",
        exoPresaleContractAbi: require("./abi/exopresale.abi.json"),
        busdTokenAddress: "",
        busdTokenAbi: require("./abi/ibep20.abi.json"),

    }
];

function getChainAddresses(chainId: number): SmartContractAddressMap {
    var chain = smartContractAddressMap.filter((x) => x.chainId == chainId)[0];
    return chain;
}

const Web3Provider: React.FC<Web3ProviderProps> = (props) => {
    var _contracts: any = {};

    const [currentChainId, setCurrentChainId] = useState(56);
    const [web3Connection, setWeb3Connection] = useState<Web3>();
    const [web3Connected, setWeb3Connected] = useState(false);
    const [walletAddress, setWalletAddress] = useState("");
    const [providerName, setProviderName] = useState("");

    //pricing
    const [bnbBalance, setBnbBalance] = useState(0);
    const [bnbUsdtPrice, setbnbUsdtPrice] = useState(0);
    const [busdAllowance, setBusdAllowance] = useState(0);
    const [busdBalance, setBusdBalance] = useState(0);

    //flags
    const [isAdmin, setIsAdmin] = useState<boolean>(false);

    //presale
    const [presaleTransactions, setPresaleTransactions] = useState<ExoPresaleTransaction[]>([]);
    const [presaleRounds, setPresaleRounds] = useState<ExoPresaleRound[]>([]);
    const [referrer, setReferrer] = useState<string>();

    //STEP 1 - once we have connected a wallet, get the address
    useEffect(() => {
        if (web3Connection != null) {
            log("useEffect[web3Connection]", "Loading wallet...");

            //get wallet address
            web3Connection.eth.getAccounts().then(function (accounts: any) {
                var account = accounts[0];
                log("useEffect[web3Connection]", `Account selected: ${account}`);

                if (account.toLowerCase() != walletAddress.toLowerCase()) {
                    setWalletAddress(account);
                    if (providerName != "walletconnect") {
                        localStorage.setItem('lastWalletAddress', account);
                        localStorage.setItem('lastWalletProvider', providerName);
                    }
                }
            });
        }
    }, [web3Connection]);


    //STEP 2 - once we have set the wallet, we'll enumerate all the plans from the contracts
    useEffect(() => {
        if (web3Connected == true && web3Connection != null) {
            fetchAllData(walletAddress);
        }
    }, [walletAddress]);

    //reviving last wallet address
    useEffect(() => {
        var address = localStorage.getItem('lastWalletAddress') || '';
        var walletProvider = localStorage.getItem('lastWalletProvider') || '';
        setReferrer(localStorage.getItem('referrer') || '');

        if (address != '') { connectWeb3(walletProvider); }
    }, []);

    function getContract(address: string, abi: any): any {
        if (web3Connected == true && web3Connection != null && address != "") {
            if (_contracts[address] == null) {

                const contract = new web3Connection.eth.Contract(abi, address);
                _contracts[address] = contract;

            }
            return _contracts[address];
        }
        return null;
    }

    async function connectWeb3(mode: string = "walletconnect") {
        var web3: any;
        var provider: any;
        setProviderName(mode);

        if (mode == "walletconnect") {
            log("connectWeb3", "Initiating session with WalletConnect...");
            provider = new WalletConnectProvider({
                rpc: {
                    56: "https://bsc-dataseed.binance.org/",
                },
                chainId: 56,
                clientMeta: {
                    name: "exoStart",
                    description: "exoStart allows you to view and contribute to next-gen crypto presales.",
                    url: "https://presale.exo.fi",
                    icons: [
                        "https://exo.fi/wp-content/uploads/2022/01/cropped-favicon-1-192x192.png"
                    ]
                }
            });
        }
        else if (mode == "metamask") {
            log("connectWeb3", "Initiating session with MetaMask...");
            provider = (window as any).ethereum;
        }

        //instantiate web3 client
        var result = await provider.enable();
        log("connectWeb3", result);
        web3 = new Web3(provider);

        if (web3 != null) {
            try {
                log("connectWeb3", "Web3 access granted!");
                setWeb3Connected(true);
                setWeb3Connection(web3 as any);

                log("connectWeb3", "Web3 loaded correctly...");
                var chainId = await web3.eth.getChainId();
                setCurrentChainId(chainId);

                //----------------------------------
                if (chainId !== 56) {
                    try {
                        await provider.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: web3.utils.toHex(56) }]
                        });
                    } catch (err: any) {
                        // This error code indicates that the chain has not been added to MetaMask
                        if (err.code === 4902 || err?.data?.orginalError?.code === 4902) {
                            await provider.request({
                                method: 'wallet_addEthereumChain',
                                params: [
                                    {
                                        chainName: 'Binance Smart Chain Mainnet',
                                        chainId: web3.utils.toHex(56),
                                        nativeCurrency: { name: 'Binance Coin', decimals: 18, symbol: 'BNB' },
                                        rpcUrls: ['https://bsc-dataseed1.binance.org'],
                                        blockExplorerUrls: [
                                            'https://bscscan.com'
                                        ]
                                    }
                                ]
                            });

                            window.location.reload();
                        }
                    }
                }
                //----------------------------------

                var chainDetails = getChainAddresses(chainId);
                if (!chainDetails) {
                    log("connectWeb3", "No chain details found...");
                    return;
                }

                //subscribe to provider events
                provider.on('accountsChanged', function (accounts: any) {
                    web3 = null;
                    provider = null;

                    connectWeb3(mode);
                });
                provider.on('disconnect', (code: number, reason: string) => {
                    console.log(code, reason);
                    disconnectWeb3();
                });

            } catch (e) {
                log("connectWeb3", "Web3 access denied...");
            }
        }
    }

    async function disconnectWeb3() {
        log("disconnectWeb3", `Disconnecting...`);
        setWeb3Connected(false);
        setWalletAddress("");
        localStorage.setItem('lastWalletAddress', "");
        localStorage.setItem('lastWalletProvider', "");
        localStorage.setItem('walletconnect', "");
        localStorage.setItem('referrer', "");
        window.location.reload();
    }

    //=======================================================================


    async function fetchAllData(address: string) {
        //exit logic on loop in case address has changed in web3 provider
        if (address.toLowerCase() != walletAddress.toLowerCase()) { return; }
        if (web3Connection != null) {
            var addresses = await web3Connection.eth.getAccounts();
            if (addresses[0].toLowerCase() != address.toLowerCase()) { return; }
        }

        //functions to refresh data
        fetchAdmins();
        // fetchBnbBalance();
        // fetchBnbUsdtTicker();
        fetchPresaleDeposits();
        fetchPresaleRounds();
        fetchBusdApprovalAmount();
        fetchBusdBalance();

        //re-run the loop
        setTimeout(() => fetchAllData(address), 30000);
    }

    async function fetchBnbBalance() {
        if (web3Connected == true && web3Connection != null) {
            // log("fetchBnbBalance", "Retrieving wallet token balance...");

            var balance = await web3Connection.eth.getBalance(walletAddress);
            // log("fetchBnbBalance", balance);

            var balanceNumber = Number(balance);
            // log("fetchBnbBalance", `Input: ${balanceNumber}`);

            setBnbBalance(balanceNumber / (1000000000000000000));
            // log("fetchBnbBalance", `Adjusted: ${bnbBalance}`);
        }
    }

    async function fetchAdmins() {
        //ideally retrieve this from an external contract such as multi-sig wallet
        var admins: string[] = [
            "0x9bf0800193825e3389B14EB69abDE07dd932b6AC" //jourdan
        ];

        var match = admins.filter((x) => x.toLowerCase() == walletAddress.toLowerCase());
        if (match.length > 0) {
            setIsAdmin(true);
        }
    }

    async function fetchBnbUsdtTicker() {
        const tickerUrl = "https://api.binance.com/api/v3/avgPrice?symbol=BNBUSDT";
        const res: BinanceTickerApiResponse = await (await fetch(tickerUrl)).json();
        setbnbUsdtPrice(Number(res.price));
    }

    async function fetchPresaleDeposits() {
        const url = `https://exo-serverless.azurewebsites.net/api/v1/presale/${walletAddress}/transactions`;
        const res: ExoPresaleTransaction[] = await (await fetch(url)).json();
        setPresaleTransactions(res);
    }

    async function fetchBusdApprovalAmount() {
        const context = getChainAddresses(currentChainId);
        if (web3Connection != null) {
            const contract = getContract(context.busdTokenAddress, context.busdTokenAbi);

            var rawAmount: number = await contract.methods.allowance(walletAddress, context.exoPresaleContractAddress).call({ from: walletAddress });
            var decimals: number = await contract.methods.decimals().call({ from: walletAddress });
            var adjustedAmount = rawAmount / (10 ** decimals);
            log("fetchBusdApprovalAmount", `Raw amount: ${rawAmount}  decimals: ${decimals}  adjustedAmount: ${adjustedAmount}`);
            setBusdAllowance(adjustedAmount);
        }
    }

    async function fetchBusdBalance() {
        const context = getChainAddresses(currentChainId);
        if (web3Connection != null) {
            const contract = getContract(context.busdTokenAddress, context.busdTokenAbi);

            var rawAmount: number = await contract.methods.balanceOf(walletAddress).call({ from: walletAddress });
            var decimals: number = await contract.methods.decimals().call({ from: walletAddress });
            var adjustedAmount = rawAmount / (10 ** decimals);
            log("fetchBusdBalance", `Raw amount: ${rawAmount}  decimals: ${decimals}  adjustedAmount: ${adjustedAmount}`);
            setBusdBalance(adjustedAmount);
        }
    }

    async function approveBusd() {
        const context = getChainAddresses(currentChainId);
        if (web3Connection != null) {
            const contract = getContract(context.busdTokenAddress, context.busdTokenAbi);
            var amount = "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";

            await contract.methods.approve(context.exoPresaleContractAddress, amount).send({ from: walletAddress });
            await fetchBusdApprovalAmount();
        }
    }

    async function fetchPresaleRounds() {
        const context = getChainAddresses(currentChainId);
        if (web3Connection != null) {
            const fullPrice = 0.12;
            const contract = getContract(context.exoPresaleContractAddress, context.exoPresaleContractAbi);
            const roundNames: string[] = ["SEED", "WHITELIST", "CRYPTONAIRZ", "SERIES A", "PUBLIC"];
            const roundPrices: number[] = [fullPrice * 0.65, fullPrice * 0.7, fullPrice * 0.7, fullPrice * 0.8, fullPrice];
            const roundVesting: string[] = ["6 MONTHS", "6 MONTHS", "6 MONTHS", "1 MONTH W/ 50% EARLY RELEASE"];
            const roundSpecial: boolean[] = [false, false, true, true, true];
            const rounds: ExoPresaleRound[] = [];

            // add seed round manually
            rounds.push({
                number: 0,
                name: "SEED",
                balance: 149350.68,
                maximum: 149350.68,
                minimum: 0,
                activeFrom: new Date("2022-04-20"),
                closed: true,
                price: fullPrice * 0.65,
                special: false,
                vesting: "9 MONTHS",
                whitelisted: true
            });

            for (var i = 1; i < 4; i++) {
                try {
                    var maximum: number = await contract.methods._roundContributionMaximums(i).call({ from: walletAddress });
                    var minimum: number = await contract.methods._roundContributionMinimums(i).call({ from: walletAddress });
                    var active: number = await contract.methods._roundActiveTimestamps(i).call({ from: walletAddress });
                    var balance: number = await contract.methods._roundContributionBalances(i).call({ from: walletAddress });
                    var whitelisted: boolean = await contract.methods._roundWhitelistAddresses(i, walletAddress).call({ from: walletAddress });
                    var factor = context.factor;

                    rounds.push({
                        number: i,
                        name: roundNames[i],
                        balance: balance * factor,
                        maximum: maximum * factor,
                        activeFrom: dateFromUtcEpoch(active),
                        minimum: minimum * factor,
                        closed: new Date() < new Date(active) || balance * factor == maximum * factor,
                        price: roundPrices[i],
                        vesting: roundVesting[i],
                        special: roundSpecial[i],
                        whitelisted: whitelisted,
                    });
                    // debugger;
                }
                catch (ex) { console.error(ex); }
            }

            setPresaleRounds(rounds);
        }
    }

    async function contribute(round: number, amount: number): Promise<void> {
        const context = getChainAddresses(currentChainId);
        if (web3Connection != null) {
            const contract = new web3Connection.eth.Contract(context.exoPresaleContractAbi, context.exoPresaleContractAddress);

            var value = Web3.utils.toBN(amount).mul(Web3.utils.toBN(1000000000000000000));
            log("contribute", `Normalised value: ${value}`);
            log("contribute", `Sending contribute tx referred by: ${referrer}`);
            await contract.methods.contribute(round, walletAddress, value, referrer).send({ from: walletAddress });

            setTimeout(async () => {
                await fetchPresaleRounds();
                fetchPresaleDeposits();
            }, 1500);
        }
    }

    async function approveContract(baseContract: string, walletAddress: string, spender: string, amount: string) {
        if (web3Connection != null) {
            const abi = require("./abi/ibep20.abi.json");
            const contract = new web3Connection.eth.Contract(abi, baseContract);

            await contract.methods.approve(spender, amount).send({ from: walletAddress });
        }
    }

    async function setReferrerString(ref: string): Promise<void> {
        if (ref && !referrer && web3Connected) {
            log("setReferrerString", `Setting referrer to: ${ref}`);
            localStorage.setItem('referrer', ref);
            setReferrer(ref);
        }
        else if (ref == referrer) { }
        else {
            log("setReferrerString", `Referrer '${referrer}' already exists. Can't add new referrer '${ref}'`);
        }
    }

    return (
        <Web3Context.Provider
            value={{
                Web3Connection: web3Connection,
                currentChainId: currentChainId,
                connectWeb3: connectWeb3,
                disconnectWeb3: disconnectWeb3,
                web3Connected: web3Connected,
                walletAddress: walletAddress,
                providerName: providerName,

                isAdmin: isAdmin,

                busdBalance: busdBalance,
                bnbBalance: bnbBalance,
                bnbUsdtPrice: bnbUsdtPrice,
                busdApprovalAmount: busdAllowance,
                approveBusd: approveBusd,

                presaleTransactions: presaleTransactions,
                presaleRounds: presaleRounds,
                contribute: contribute,
                setReferrerString: setReferrerString,
            }}
        >
            {props.children}
        </Web3Context.Provider>
    );
}

export default Web3Provider;